import { useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { AnonSignInPrompt } from '@/components/AnonSignInPrompt';
import { useDevBannerOverrides } from '@/lib/devBannerTriggers';
import { CommandQueueProcessor } from '@/components/CommandQueueProcessor';
import { useAppSession } from '@/contexts/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { useAnonSession } from '@/hooks/useAnonSession';
import { useAnonHome } from '@/hooks/useAnonHome';
import { AgentCanvasToolbarSlotProvider } from '@/components/agentCanvasToolbarSlot';
import { SidebarRail, useSidebarRailActive } from './SidebarRail';
import { sessionsToRailItems } from './railItems';
import { ConditionalMobileDevToggle, ConditionalRailDevToggle } from '@/components/DevPanel';
import { PaperArtifactPanel } from './PaperArtifactPanel';
import { PaperArtifactRail } from './PaperArtifactRail';
import { PaperMobileTabBar } from './PaperMobileTabBar';
import { pruneCodeCollapsedToSessions } from './PaperMessageCard';

export const PaperChatLayout = () => {
  const navigate = useNavigate();
  const { session, actions, artifacts } = useAppSession();
  const { chat } = session;
  const auth = useAuth();
  const { onHome, anonPrompt } = useAnonHome();
  const devOverrides = useDevBannerOverrides();
  const railActive = useSidebarRailActive();

  const account = useMemo(() => {
    if (!auth.isAuthenticated || !auth.user) return undefined;
    const u = auth.user;
    return {
      initials: `${u.firstName?.charAt(0) ?? ''}${u.lastName?.charAt(0) ?? ''}`,
      displayName: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || (u.email ?? 'Account'),
      metaLine: u.email ?? '',
      onSettings: () => navigate('/settings'),
      onLogout: () => auth.logout(),
    };
  }, [auth, navigate]);

  const anonHasStartedSession = useMemo(
    () =>
      !auth.isAuthenticated &&
      !!chat.activeChat &&
      chat.activeChat.timeline.some((item) => item.kind === 'user'),
    [auth.isAuthenticated, chat.activeChat],
  );

  useAnonSession({
    isAuthenticated: auth.isAuthenticated,
    signIn: auth.signIn,
    anonHasStartedSession,
  });

  // Prune collapsed-code-card storage for sessions that no longer exist
  // (deleted, etc.). Guarded against transient empty-session states so we
  // don't wipe everything during initial load.
  useEffect(() => {
    const sessions = session.chatHistory.sessions;
    if (sessions.length === 0) return;
    pruneCodeCollapsedToSessions(new Set(sessions.map((s) => s.id)));
  }, [session.chatHistory.sessions]);

  // Right rail is only shown when we're in an active conversation AND that
  // conversation has artifact components. Landing page / empty new chats
  // never show the rail, so the layout stays two-column there.
  // Left sidebar is hidden for anonymous users on the landing page —
  // Screen 01 treatment. Authenticated users always get the rail; any
  // active chat also shows it (so anon-mid-conversation still navigates).
  const showSidebar = auth.isAuthenticated || !!chat.activeChatId;
  const forceArtifact = devOverrides.forceArtifact;
  const showArtifactRail = forceArtifact || (!!chat.activeChatId && artifacts.hasArtifact);
  const artifactOpen = forceArtifact || (showArtifactRail && artifacts.artifactOpen);
  const maximized = artifacts.artifactDesktopMaximized;

  const railItems = useMemo(
    () => sessionsToRailItems(session.chatHistory.sessions, chat.activeChatId ?? null),
    [session.chatHistory.sessions, chat.activeChatId],
  );

  // The path-derived rail active returns 'chat' for both `/` and
  // `/agent/<id>`. Only keep that highlight when the active session has
  // no title yet — once a session is named, it gets its own row in
  // pinned/recent and "New chat" should drop back to the resting state.
  const activeSession = useMemo(() => {
    if (!chat.activeChatId) return null;
    return session.chatHistory.sessions.find((s) => s.id === chat.activeChatId) ?? null;
  }, [chat.activeChatId, session.chatHistory.sessions]);
  const effectiveRailActive =
    railActive === 'chat' && activeSession?.title ? undefined : railActive;

  const agentTitle = useMemo(() => {
    if (!chat.activeChatId) return null;
    return session.chatHistory.sessions.find((s) => s.id === chat.activeChatId)?.title ?? null;
  }, [chat.activeChatId, session.chatHistory.sessions]);

  return (
    <>
      <CommandQueueProcessor handleSend={session.handleSend} stop={chat.stop} />

      <AgentCanvasToolbarSlotProvider>
      <div
        className="absolute inset-0 flex flex-col bg-[var(--bg)] text-[var(--fg)]"
        style={{ fontFamily: 'var(--font-sans-paper)' }}
      >
        <div className="flex-1 min-h-0 flex">
          {showSidebar && (
            // SidebarRail is hover-expand and laid out horizontally; the
            // mobile equivalent is the bottom tab bar below, so hide the rail
            // entirely on mobile. `contents` keeps the wrapper out of layout
            // on desktop so the rail sits flush against the chat column.
            <div className="hidden desktop:contents">
              <SidebarRail
                active={effectiveRailActive}
                items={auth.isAuthenticated ? undefined : ['chat']}
                pinned={auth.isAuthenticated ? railItems.pinned : []}
                recents={auth.isAuthenticated ? railItems.recents : []}
                onNavigate={(id) => {
                  if (id === 'chat') onHome();
                  if (id === 'agents') navigate('/agents');
                  if (id === 'settings') navigate('/settings');
                }}
                onItemSelect={(sessionId) => {
                  void actions.handleSelectAgent(sessionId);
                }}
                bottomSlot={(expanded) => <ConditionalRailDevToggle expanded={expanded} />}
                account={account}
              />
            </div>
          )}

          {/* Chat column — hidden when the artifact panel is maximized
              (desktop) or whenever the artifact panel is open (mobile, where
              there's no room for both columns side-by-side). */}
          {(!artifactOpen || !maximized) && (
            <main
              className={cn(
                'flex-1 flex flex-col min-w-0 relative',
                artifactOpen && 'hidden desktop:flex',
              )}
              style={{ borderRight: artifactOpen ? '1px solid var(--hair)' : 'none' }}
            >
              <Outlet />
            </main>
          )}

          {/* Artifact panel body — only when open */}
          {artifactOpen && (
            <PaperArtifactPanel
              engine={artifacts.artifactEngine}
              maximized={maximized}
              onMaximizedChange={artifacts.setArtifactDesktopMaximized}
              onClose={() => artifacts.setArtifactOpen(false)}
              agentTitle={agentTitle}
            />
          )}

          {/* Right rail — flips between two single-purpose rounded rectangles:
              open-panel chevron when the panel is closed, canvas tools
              rail when it's open. The close (X) lives inside the panel's
              chrome instead of the rail so the canvas tools stay
              tightly focused on drawing controls. Hidden on mobile, where
              the artifact UI lives elsewhere. */}
          {showArtifactRail && (
            <div className="hidden desktop:contents">
              <PaperArtifactRail
                artifactOpen={artifacts.artifactOpen}
                onOpen={() => artifacts.setArtifactOpen(true)}
              />
            </div>
          )}
        </div>

        {/* Tab bar mirrors the desktop SidebarRail's auth gating: anon users
            (even mid-conversation) get no app-chrome navigation, the same way
            the rail only shows the Chat row for them. */}
        {auth.isAuthenticated && (
          <PaperMobileTabBar
            className="desktop:hidden"
            active="chat"
            devControlSlot={<ConditionalMobileDevToggle />}
          />
        )}
      </div>
      </AgentCanvasToolbarSlotProvider>

      {(anonPrompt.visible || devOverrides.anonSignIn) && (
        <AnonSignInPrompt
          onSignIn={anonPrompt.onSignIn}
          onContinue={anonPrompt.onContinue}
          onCancel={anonPrompt.onCancel}
        />
      )}
    </>
  );
};
